import { useEffect, useRef } from 'react';

export interface HotkeyMap {
  /** Map a key signature to a handler. Signature: "f2", "ctrl+p", "+", "Escape". */
  [signature: string]: (event: KeyboardEvent) => void;
}

function signatureOf(e: KeyboardEvent): string {
  const parts: string[] = [];
  if (e.ctrlKey || e.metaKey) parts.push('ctrl');
  if (e.altKey) parts.push('alt');
  if (e.shiftKey && e.key.length > 1) parts.push('shift');
  parts.push(e.key.toLowerCase());
  return parts.join('+');
}

/**
 * Global keyboard-shortcut binder. Ignores keystrokes while a text input /
 * textarea / contentEditable is focused, EXCEPT for Escape and function keys
 * (F2/F4/F8/F9) which always fire so a clerk can drive the till hands-on-keys.
 */
export function useHotkeys(map: HotkeyMap, enabled = true): void {
  const mapRef = useRef(map);
  mapRef.current = map;

  useEffect(() => {
    if (!enabled) return;

    function onKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      const isEditable =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable === true;

      const isAlwaysOn = e.key === 'Escape' || /^F\d+$/.test(e.key);
      if (isEditable && !isAlwaysOn) return;

      const sig = signatureOf(e);
      const handler = mapRef.current[sig];
      if (handler) {
        e.preventDefault();
        handler(e);
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
}
